import type { BookingRow, ExpenseRow } from "./types";

export type BookingStatus = "all" | "open" | "invoiced";
export interface BookingFilters { month: string | null; client: number | null; status: BookingStatus; q: string }

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const BOOKING_STATUSES: BookingStatus[] = ["all", "open", "invoiced"];

/** Default view: everything still open, across all months (the working list). A month is analysis. */
export function readBookingFilters(sp: URLSearchParams): BookingFilters {
  const m = sp.get("month");
  const client = Number(sp.get("client"));
  const status = sp.get("status") as BookingStatus;
  return {
    month: m && MONTH.test(m) ? m : null,
    client: Number.isInteger(client) && client > 0 ? client : null,
    status: BOOKING_STATUSES.includes(status) ? status : "open",
    q: sp.get("q") ?? "",
  };
}

export function writeBookingFilters(f: BookingFilters): URLSearchParams {
  const sp = new URLSearchParams();
  if (f.month !== null) sp.set("month", f.month);
  if (f.client !== null) sp.set("client", String(f.client));
  if (f.status !== "open") sp.set("status", f.status);
  if (f.q) sp.set("q", f.q);
  return sp;
}

export function bookingsQuery(f: BookingFilters): string {
  const sp = new URLSearchParams();
  if (f.month) sp.set("month", f.month);
  if (f.client !== null) sp.set("client", String(f.client));
  sp.set("status", f.status);
  return `/api/entries?${sp}`;
}

const has = (hay: (string | null | undefined)[], q: string) => {
  const needle = q.trim().toLowerCase();
  return !needle || hay.some((h) => h?.toLowerCase().includes(needle));
};

export function matchesBooking(row: BookingRow, q: string): boolean {
  return has([row.description, row.clientName, row.projectName, row.invoiceNumber], q);
}

export function totals(rows: BookingRow[]) {
  const sum = (f: (r: BookingRow) => number) => rows.reduce((s, r) => s + f(r), 0);
  return {
    count: rows.length,
    trackedMinutes: sum((r) => r.trackedMinutes),
    billableMinutes: sum((r) => r.billableMinutes),
    amountEur: Math.round(sum((r) => r.amountEur) * 100) / 100,
  };
}

export type ExpenseStatus = "open" | "classified" | "all";
export interface ExpenseFilters { status: ExpenseStatus; month: string | null; q: string }
const EXPENSE_STATUSES: ExpenseStatus[] = ["open", "classified", "all"];

export function readExpenseFilters(sp: URLSearchParams): ExpenseFilters {
  const status = sp.get("status") as ExpenseStatus;
  const m = sp.get("month");
  return { status: EXPENSE_STATUSES.includes(status) ? status : "open", month: m && MONTH.test(m) ? m : null, q: sp.get("q") ?? "" };
}

export function writeExpenseFilters(f: ExpenseFilters): URLSearchParams {
  const sp = new URLSearchParams();
  if (f.status !== "open") sp.set("status", f.status);
  if (f.month) sp.set("month", f.month);
  if (f.q) sp.set("q", f.q);
  return sp;
}

export function matchesExpense(row: ExpenseRow, q: string): boolean {
  return has([row.payee, row.purpose, row.note, ...row.shares.flatMap((s) => [s.client_name, s.rebill_ref])], q);
}
